import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScenarioEditMessageFormMobile.module.css';

interface ScenarioEditMessageFormMobileProps {
  content: string;
  onSave: (newContent: string) => Promise<void>;
  onClose: () => void;
  isVisible: boolean;
  maxLength?: number;
}

const ScenarioEditMessageFormMobile: React.FC<ScenarioEditMessageFormMobileProps> = ({ 
  content,
  onSave,
  onClose,
  isVisible,
  maxLength
}) => {
  const [editedContent, setEditedContent] = useState<string>(content);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isAnimatedIn, setIsAnimatedIn] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Reset content when message changes
  useEffect(() => {
    setEditedContent(content);
  }, [content]);
  
  // Animation effect when component mounts
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure DOM is ready for the animation
      const timer = setTimeout(() => {
        setIsAnimatedIn(true);
        // Focus the textarea after animation
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 300);
      }, 10);
      
      // Prevent body scrolling when popup is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isVisible]);
  
  // Handle close with animation
  const handleClose = (): void => {
    setIsAnimatedIn(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      document.body.style.overflow = '';
      onClose();
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (editedContent.trim() === content) {
      handleClose();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editedContent.trim());
      handleClose();
    } catch (err) {
      console.error('Failed to save message:', err);
      // Could add error state handling here
    } finally {
      setIsSaving(false);
    }
  };

  const getCharCountClass = () => {
    if (!maxLength) return '';
    const ratio = editedContent.length / maxLength;
    if (ratio >= 1) return styles.charCountError;
    if (ratio >= 0.9) return styles.charCountWarning;
    return '';
  };

  if (!isVisible) return null;

  return createPortal(
    <div 
      className={`${styles.backdrop} ${isAnimatedIn ? styles.backdropVisible : ''}`}
      onClick={handleClose}
    >
      <div 
        className={`${styles.popupContainer} ${isAnimatedIn ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.handle}></div>
          <h3 className={styles.title}>Edit Message</h3>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <textarea
            ref={textareaRef}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className={styles.textarea}
            disabled={isSaving}
            placeholder="Edit your message..."
          />
          
          {maxLength && (
            <div className={styles.charCountContainer}>
              <span className={`${styles.charCount} ${getCharCountClass()}`}>
                {editedContent.length}/{maxLength}
              </span>
            </div>
          )}
          
          <div className={styles.buttons}>
            <button 
              type="button" 
              onClick={handleClose} 
              className={styles.cancelButton}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`${styles.saveButton} ${isSaving ? styles.saving : ''}`}
              disabled={
                editedContent.trim() === content || 
                isSaving || 
                (maxLength ? editedContent.length > maxLength : false)
              }
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ScenarioEditMessageFormMobile; 